import CampaignIcon from '@mui/icons-material/Campaign';
import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';

import MessageModal from '@/components/MessageModal/index';

export interface MessageItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: string;
  color: string;
}

interface RunningMessagesBannerProps {
  messages: MessageItem[];
}

/** Marquee travel speed. Keeps long and short message sets moving at one pace. */
const SPEED_PX_PER_SEC = 60;

const BannerContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  padding: '0 16px',
  display: 'flex',
  alignItems: 'center',
  minHeight: '48px',
}));

/**
 * Holds two identical sequences side by side. Shifting the track by -50% lands
 * the copy exactly where the original started, so the loop has no visible seam.
 */
const MarqueeTrack = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: 'max-content',
  willChange: 'transform',
  animationName: 'runningMessagesMarquee',
  animationTimingFunction: 'linear',
  animationIterationCount: 'infinite',
  '@keyframes runningMessagesMarquee': {
    '0%': { transform: 'translateX(0)' },
    '100%': { transform: 'translateX(-50%)' },
  },
});

const Sequence = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

const MessageEntry = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  paddingRight: '32px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': {
    opacity: 0.75,
  },
});

/** Pinned beside the marquee, so the text scrolls past it rather than under it. */
const StickyIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minWidth: '24px',
  marginRight: '16px',
});

/** Carries each message's severity colour, which the shared icon can't. */
const ColorDot = styled(Box)({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  flexShrink: 0,
});

/** Clips the marquee so it can't spill over the pinned icon. */
const MarqueeViewport = styled(Box)({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
});

const RunningMessagesBanner: React.FC<RunningMessagesBannerProps> = ({
  messages,
}) => {
  const theme = useTheme();
  const [selectedMessage, setSelectedMessage] = useState<MessageItem | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [paused, setPaused] = useState(false);
  const sequenceRef = useRef<HTMLDivElement | null>(null);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const measure = () => {
      const width = sequenceRef.current?.scrollWidth ?? 0;
      setDuration(width / SPEED_PX_PER_SEC);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [messages]);

  const handleMessageClick = (message: MessageItem) => {
    setSelectedMessage(message);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMessage(null);
  };

  if (!messages || messages.length === 0) {
    return null;
  }

  const sequence = (isClone: boolean) => (
    <Sequence
      ref={isClone ? undefined : sequenceRef}
      aria-hidden={isClone || undefined}
    >
      {messages.map(message => (
        <MessageEntry
          key={message.id}
          onClick={() => handleMessageClick(message)}
        >
          <ColorDot sx={{ backgroundColor: message.color }} />
          <Typography component='span' sx={{ whiteSpace: 'nowrap' }}>
            {message.content}
          </Typography>
        </MessageEntry>
      ))}
    </Sequence>
  );

  return (
    <>
      <BannerContainer
        data-banner
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        sx={{
          '&:hover': {
            boxShadow: theme.shadows[4],
          },
          transition: 'box-shadow 0.3s ease',
        }}
      >
        <StickyIcon
          data-testid='running-messages-icon'
          sx={{ color: theme.palette.text.secondary }}
        >
          <CampaignIcon />
        </StickyIcon>

        <MarqueeViewport>
          <MarqueeTrack
            data-testid='running-messages-track'
            sx={{
              animationDuration: `${duration}s`,
              animationPlayState: paused ? 'paused' : 'running',
            }}
          >
            {sequence(false)}
            {sequence(true)}
          </MarqueeTrack>
        </MarqueeViewport>
      </BannerContainer>

      {selectedMessage && (
        <MessageModal
          open={modalOpen}
          onClose={handleCloseModal}
          title={selectedMessage.title}
          message={selectedMessage.content}
          icon={selectedMessage.icon}
          iconColor={selectedMessage.color}
        />
      )}
    </>
  );
};

export default RunningMessagesBanner;
